import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertService, ENV, Environment } from 'lib-trend-core';
import { isSafeChatAppPath } from 'lib-trend-core/chat';
import { ReCaptcha2Component } from 'ngx-captcha';
import { ChatAppSessionService } from '../../core/chat-app-session.service';

/** i18n key of the banner explaining why the user is signing in again. */
const REASON_KEYS: Record<string, string> = {
  expired: 'chatApp.signIn.reasonExpired',
  revoked: 'chatApp.signIn.reasonRevoked',
};

/**
 * Chat app sign-in — SPEC-2026-09-internal-chat-pwa (T-32 · RF-04). The product's
 * credentials and human verification; the company is the one the product's sign-in
 * would select (it comes from the token the API mints). After signing in the user lands
 * on the address they originally asked for, when that address belongs to the chat app.
 */
@Component({
  selector: 'chat-app-sign-in',
  templateUrl: './chat-app-sign-in.component.html',
  styleUrls: ['./chat-app-sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAppSignInComponent implements OnInit {

  @ViewChild('captchaElem') captchaElem?: ReCaptcha2Component;

  readonly form: FormGroup;

  submitting = false;

  passwordHidden = true;

  reasonKey: string | null = null;

  errorMessage: string | null = null;

  private returnUrl = '/';

  private readonly route: ActivatedRoute;
  private readonly session: ChatAppSessionService;
  private readonly alertService: AlertService;
  private readonly environment: Environment;

  constructor(injector: Injector, formBuilder: FormBuilder) {
    this.route = injector.get(ActivatedRoute);
    this.session = injector.get(ChatAppSessionService);
    this.alertService = injector.get(AlertService);
    this.environment = injector.get(ENV);
    this.form = formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      recaptcha: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const returnUrl = params.get('returnUrl');
    this.returnUrl = isSafeChatAppPath(returnUrl) ? returnUrl : '/';
    this.reasonKey = REASON_KEYS[params.get('reason') ?? ''] ?? null;
  }

  get recaptchaSiteKey(): string {
    return String(this.environment['recaptchaToken'] ?? '');
  }

  get productUrl(): string {
    return String(this.environment['trendWebAppUrl'] ?? '');
  }

  get productLoginUrl(): string {
    return `${this.productUrl.replace(/\/+$/, '')}/login`;
  }

  isInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    this.errorMessage = null;
    const { email, password, recaptcha } = this.form.value;
    try {
      const user = await this.session.signIn(String(email).trim(), password, recaptcha);
      if (!this.session.hasAllowedRole(user)) {
        window.location.assign('/unavailable?reason=role');
        return;
      }
      if (!this.session.hasChatModule(user)) {
        window.location.assign('/unavailable?reason=module');
        return;
      }
      // A full load boots the app with the new session and nothing of a previous one.
      window.location.assign(this.returnUrl);
    } catch (error: any) {
      this.submitting = false;
      this.resetCaptcha();
      if (error?.error?.key === 'chat.errors.moduleDisabled') {
        window.location.assign('/unavailable?reason=module');
        return;
      }
      this.errorMessage = error?.error?.message || null;
      if (!this.errorMessage) {
        this.alertService.error();
      }
    }
  }

  onCaptchaExpire(): void {
    this.form.get('recaptcha')?.reset();
  }

  private resetCaptcha(): void {
    this.form.get('recaptcha')?.setValue(null);
    this.captchaElem?.resetCaptcha();
  }
}
